import { Alert, AlertTitle, Button, Typography } from '@mui/material';

import { ApiNetworkError, ApiRequestError } from '../api/errors';
import { useT } from '../i18n/LanguageContext';

/**
 * The one place an error becomes something a person reads.
 *
 * Nothing reaches the screen unless somebody wrote it for a person: the API's own messages qualify — they
 * come from its catalogue in the language we asked for. A browser's `TypeError` or a stack does not.
 * `onRetry` should be passed wherever the request can simply be made again.
 */
export const ErrorNotice = ({ error, onRetry }: { error: unknown; onRetry?: () => void }) => {
  const t = useT();

  if (!error) {
    return null;
  }

  const retry =
    onRetry === undefined ? null : (
      <Button size="small" color="inherit" onClick={onRetry} sx={{ mt: 1, display: 'block' }}>
        {t('common.retry')}
      </Button>
    );

  if (error instanceof ApiNetworkError) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ mb: 2.5 }}>
        <AlertTitle sx={{ mb: 0.25 }}>{t('error.network.title')}</AlertTitle>
        {t('error.network.detail', { url: error.baseUrl })}
        {retry}
      </Alert>
    );
  }

  if (error instanceof ApiRequestError) {
    return (
      <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
        <AlertTitle sx={{ mb: 0.25 }}>{t('error.request.title', { status: error.status })}</AlertTitle>
        {error.message}
        {error.correlationId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            correlation id {error.correlationId}
          </Typography>
        ) : null}
        {retry}
      </Alert>
    );
  }

  // eslint-disable-next-line no-console
  console.error('[bo-admin] Unhandled error surfaced to the UI:', error);

  return (
    <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
      <AlertTitle sx={{ mb: 0.25 }}>{t('error.unexpected.title')}</AlertTitle>
      {t('error.unexpected.detail')}
      {retry}
    </Alert>
  );
};
